import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryOneOnOneHistoryParams } from '../../../services/api/dataLibrary';
import type {
  DataLibraryOneOnOneHistoryRow,
  DataLibraryOneOnOneHistoryFilterOptions,
  OneOnOneHistoryEvent,
} from '../../../types';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import {
  ONE_ON_ONE_HISTORY_COLUMNS,
  ONE_ON_ONE_HISTORY_EVENT_LABELS,
  SCHEDULING_METHOD_LABELS,
} from './oneOnOneHistoryColumns';
import { missEntryText } from '../../coaches/missClaimText';
import {
  DATA_LIBRARY_ONE_ON_ONE_TYPE_LABELS,
  ONE_ON_ONE_STATUS_LABELS,
} from '../one-on-ones/oneOnOneColumns';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

/** A row with its codes turned into labels, so cells and Group By read the same text. */
export type OneOnOneHistoryDisplayRow = Omit<
  DataLibraryOneOnOneHistoryRow,
  'event' | 'oneOnOneType' | 'schedulingMethod' | 'status'
> & {
  event: string;
  /** The API event code, for the badge color */
  eventCode: OneOnOneHistoryEvent;
  /** The status label, as on the 1:1s tab */
  status: string;
  oneOnOneType: string | null;
  schedulingMethod: string | null;
  /** "No show - paid", or null when the change made no claim */
  miss: string | null;
};

const EMPTY_OPTIONS: DataLibraryOneOnOneHistoryFilterOptions = { cohorts: [], coaches: [], participants: [], events: [] };

export function useDataLibraryOneOnOneHistory({
  selectedCohortIds,
  initialFilters,
}: {
  selectedCohortIds: string[];
  initialFilters?: ActiveFilterRow[];
}) {
  const [data, setData] = useState<DataLibraryOneOnOneHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryOneOnOneHistoryFilterOptions>(EMPTY_OPTIONS);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  // The API sorts by change time only, so the sort holds the order and nothing else.
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', order: 'DESC' });
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>(initialFilters ?? []);
  const [activeGroup, setActiveGroup] = useState<GroupState | null>(null);
  const { columns, setColumns } = useDataLibraryColumns(ONE_ON_ONE_HISTORY_COLUMNS);
  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  const filterParams = useMemo(() => {
    const params: Omit<DataLibraryOneOnOneHistoryParams, 'cohortIds'> & { cohortId?: string } = {};
    if (debouncedSearch) params.search = debouncedSearch;
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'cohort') params.cohortId = filter.values[0];
      if (filter.key === 'coach') params.coachId = filter.values[0];
      if (filter.key === 'participant') params.participantId = filter.values[0];
      if (filter.key === 'event') params.event = filter.values[0];
    }
    return params;
  }, [debouncedSearch, activeFilters]);

  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  const latestRequest = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const { cohortId, ...rest } = filterParams;
    // The Cohort filter narrows the selected cohorts to one.
    const cohortIds = cohortId ? selectedCohortIds.filter(id => id === cohortId) : selectedCohortIds;
    if (cohortIds.length === 0) {
      setData([]);
      setTotalCount(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { cohortIds, sortOrder: sortConfig.order, ...rest };
      const fetchPage = (p: number, limit: number) =>
        dataLibraryApi
          .getOneOnOneHistory({ ...params, page: p, limit })
          .then(r => ({ ...r, meta: { pagination: r.pagination } }));
      const result = isGrouped
        ? await fetchAllPages(fetchPage, () => requestId !== latestRequest.current)
        : await fetchPage(page, pageSize);
      if (requestId !== latestRequest.current) return;
      const total = result.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setData(result.data || []);
      setTotalCount(total);
      setTotalPages(result.pagination.totalPages);
      setFilterOptions(result.filterOptions ?? EMPTY_OPTIONS);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({ title: 'Error', message: 'Failed to load 1:1 history', color: 'red' });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, page, pageSize, sortConfig, filterParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, selectedCohortIds, activeGroup]);

  // A participant belongs to the cohorts it came from, so a cohort change drops that filter.
  const firstCohorts = useRef(selectedCohortIds);
  useEffect(() => {
    if (selectedCohortIds === firstCohorts.current) return;
    setActiveFilters(prev => prev.filter(f => f.key !== 'participant'));
  }, [selectedCohortIds]);

  const handleSort = useCallback(() => {
    setSortConfig(prev => ({ key: 'createdAt', order: prev.order === 'ASC' ? 'DESC' : 'ASC' }));
  }, []);

  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => (prev.some(f => f.key === key) ? prev : [...prev, { key, values: [] }]));
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters = activeFilters.some(f => f.values.length > 0) || debouncedSearch.length > 0;

  const rows: OneOnOneHistoryDisplayRow[] = useMemo(
    () =>
      data.map(row => ({
        ...row,
        event: ONE_ON_ONE_HISTORY_EVENT_LABELS[row.event] ?? row.event,
        eventCode: row.event,
        status: ONE_ON_ONE_STATUS_LABELS[row.status] ?? row.status,
        oneOnOneType: row.oneOnOneType
          ? DATA_LIBRARY_ONE_ON_ONE_TYPE_LABELS[row.oneOnOneType] ?? row.oneOnOneType
          : null,
        // F2: no booking (an undated 1:1) shows the empty marker in "Booked via".
        schedulingMethod:
          row.toStart && row.schedulingMethod
            ? SCHEDULING_METHOD_LABELS[row.schedulingMethod] ?? row.schedulingMethod
            : null,
        miss: row.missKind && row.missResult ? missEntryText({ kind: row.missKind, result: row.missResult }) : null,
      })),
    [data],
  );

  const groupedRows = useMemo(
    () =>
      groupDataByLevels(
        rows as unknown as Record<string, unknown>[],
        isGrouped ? activeGroup : null,
        columns,
      ) as GroupNode<OneOnOneHistoryDisplayRow>[] | null,
    [rows, activeGroup, isGrouped, columns],
  );

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows,
    groupedRows,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
  };
}
